import styled from 'styled-components'
import { rem } from 'polished'
import Sheet from 'components/utils/Sheet'
import TextButton from 'LuxKit/Button/TextButton'
import { STOP_TIMING_STICKY_HEADER_Z_INDEX } from '../components/stopTimingLayers'
import {
  CHAT_MAX_WIDTH,
  CHAT_MIN_WIDTH,
  LEFT_PANE_WIDTH_VAR,
  RIGHT_PANEL_MIN_WIDTH,
} from '../components/constants'

export {
  CHAT_DEFAULT_WIDTH,
  CHAT_MAX_WIDTH,
  CHAT_MIN_WIDTH,
  RIGHT_PANEL_MIN_WIDTH,
  LEFT_PANE_WIDTH_VAR,
  SHOW_ON_MAP_ZOOM,
  PLACE_SHOW_ON_MAP_ZOOM,
} from '../components/constants'

export const TripPlannerShell = styled.div`
  display: flex;
  block-size: 100vh;
  overflow: hidden;
`

export const MainLayout = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-inline-size: 0;
  overflow: hidden;
`

export const ContentWrapper = styled.div`
  --right-panel-min-width: ${rem(RIGHT_PANEL_MIN_WIDTH)};

  display: grid;
  grid-template-columns: 1fr;
  flex: 1;
  min-block-size: 0;
`

export const MainContent = styled.div`
  display: grid;
  grid-template-columns:
    clamp(
      ${rem(CHAT_MIN_WIDTH)},
      var(${LEFT_PANE_WIDTH_VAR}),
      max(
        ${rem(CHAT_MIN_WIDTH)},
        min(${rem(CHAT_MAX_WIDTH)}, calc(100% - var(--right-panel-min-width)))
      )
    )
    0
    1fr;
  min-block-size: 0;
  block-size: 100%;
  overflow-x: auto;
`

export const ChatColumnLayout = styled.div`
  display: flex;
  flex-direction: column;
  min-block-size: 0;
  block-size: 100%;
  border-inline-end: 1px solid ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
`

export const RightPanel = styled.div`
  display: flex;
  flex-direction: column;
  min-block-size: 0;
  min-inline-size: ${rem(RIGHT_PANEL_MIN_WIDTH)};
  block-size: 100%;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  position: relative;
  overflow: hidden;
`

/** Fills the right panel; used to measure width without sharing the portal-target ref. */
export const RightPanelLayoutMeasure = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-inline-size: 0;
  min-block-size: 0;
  inline-size: 100%;
  block-size: 100%;
`

export const MAP_KEEP_ALIVE_HIDDEN_CLASS = 'keep-alive-hidden'

export const FullHeightMapWrapper = styled.div`
  flex: 1;
  min-block-size: 0;
  display: flex;
  block-size: 100%;
  overflow: hidden;
  position: relative;

  /* Keep Mapbox mounted across view toggles without taking layout or clicks.
     Descendants stay hidden too: FilterChip (and similar chrome) sets
     visibility: visible, which would otherwise paint over Timeline/Chat. */
  &.${MAP_KEEP_ALIVE_HIDDEN_CLASS} {
    position: absolute;
    inset: 0;
    z-index: 0;
    visibility: hidden;
    pointer-events: none;
  }

  &.${MAP_KEEP_ALIVE_HIDDEN_CLASS} * {
    visibility: hidden !important;
  }
`

export const MapArea = styled.div`
  flex: 1;
  min-inline-size: 0;
  position: relative;
  block-size: 100%;
  overflow: hidden;

  /* Force the ChatMapWidget to fill this container absolutely */
  & > .fill-container {
    position: absolute;
    inset: 0;
  }
`

export const MapOverlayStack = styled.div`
  position: absolute;
  inset-block-start: ${rem(12)};
  inset-inline: ${rem(12)};
  z-index: 6;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(8)};
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`

export const MapUpdatingIndicatorPill = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-block-start: ${rem(12)};
  padding: 0 ${rem(13)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border-radius: ${props => props.theme.borderRadius.round};
  box-shadow: ${props => props.theme.shadow.flat.small};
  block-size: ${rem(44)};
  pointer-events: none;
`

export const SearchThisAreaButton = styled(TextButton)`
  box-shadow: ${props => props.theme.shadow.flat.small};
`

/* Absolute so the view toggle stays centred on the panel rather than on the space beside it. */
export const RightPanelHeaderLeadingControl = styled.div`
  position: absolute;
  inset-inline-start: ${rem(16)};
  inset-block-start: 50%;
  transform: translateY(-50%);
`

export const RightPanelHeader = styled.div`
  position: relative;
  z-index: ${STOP_TIMING_STICKY_HEADER_Z_INDEX};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${rem(10)} ${rem(16)};
  border-block-end: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};

  /* The leading control is absolute so the toggle stays centred on the panel, which means nothing
     reserves room for it. Symmetric padding keeps the toggle centred and still clears the control
     on a panel narrow enough for the two to meet. */
  &.has-leading-control {
    padding-inline: ${rem(64)};
  }
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  flex-shrink: 0;

  && fieldset > div {
    grid-auto-columns: 1fr;
  }
`

export const RightPanelContent = styled.div`
  flex: 1;
  min-block-size: 0;
  display: flex;
  flex-direction: column;
  position: relative;
`

export const EmptyStateWrapper = styled(Sheet)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  block-size: 100%;
`

export const DayItineraryOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`

export const InputWrapper = styled.div`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  inline-size: 100%;
  min-inline-size: 0;
  padding-block-start: ${rem(16)};
  padding-inline: ${rem(16)};
  padding-block-end: ${rem(12)};

  &.has-context-bar {
    padding-block-start: 0;
  }

  &.flush-bottom {
    padding-block-end: 0;
  }
`

export const LandingPageWrapper = styled.div`
  inline-size: 100%;
  display: flex;
  justify-content: center;
  flex: 1;
  min-block-size: 0;
`
